import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  EyeOff,
  Ghost,
  Check,
  Minus,
  ArrowRight,
  Lock,
} from "lucide-react";
import { TIERS, TIER_LOGOS, formatAUD, HERO_IMAGES } from "../mock";

const tierIcon = { degoogled: ShieldCheck, stealth: EyeOff, phantom: Ghost };

// Each tier logo needs a treatment suited to its native art:
//   Stealth  \u2192 invert so its black letters become white on the dark card
//   Phantom  \u2192 native (dark bg blends into card, purple outline pops)
//   Degoogled\u2192 small elegant white \"plaque\" \u2014 Google-parody needs a light
//              surface to remain legible, so we frame it as an intentional
//              certification badge instead of a giant white box.
const TierWordmark = ({ tierId, size = "lg" }) => {
  // New tier logos: full-colour Degoogled + white/purple Phantom wordmarks
  // stand on any dark background with just a purple drop-shadow.
  // Stealth still uses the older mono badge, so it keeps the invert treatment.
  const heightClass = size === "lg"
    ? { degoogled: "h-10", stealth: "h-12", phantom: "h-16" }[tierId]
    : { degoogled: "h-6",  stealth: "h-6",  phantom: "h-8"  }[tierId];

  if (tierId === "stealth") {
    return (
      <img
        src={TIER_LOGOS.stealth}
        alt="Stealth"
        className={`${heightClass} w-auto`}
        style={{ filter: "invert(1) drop-shadow(0 0 12px rgba(168,85,247,0.35))" }}
      />
    );
  }

  return (
    <img
      src={TIER_LOGOS[tierId]}
      alt={tierId === "degoogled" ? "Degoogled" : "Phantom"}
      className={`${heightClass} w-auto`}
      style={{ filter: "drop-shadow(0 0 18px rgba(168,85,247,0.55))" }}
    />
  );
};

// Compare Models grid — feature matrix
const COMPARE_MATRIX = [
  { feature: "GrapheneOS clean install", degoogled: true, stealth: true, phantom: true },
  { feature: "Google services & telemetry removed", degoogled: true, stealth: true, phantom: true },
  { feature: "F-Droid + Aurora Store pre-installed", degoogled: true, stealth: true, phantom: true },
  { feature: "Full-disk encryption", degoogled: true, stealth: true, phantom: true },
  { feature: "Baseline hardening profile", degoogled: true, stealth: true, phantom: true },

  { feature: "MAC & IMEI randomization on boot", degoogled: false, stealth: true, phantom: true },
  { feature: "DNS-over-HTTPS resolver", degoogled: false, stealth: true, phantom: true },
  { feature: "Encrypted VPN pre-configured", degoogled: false, stealth: true, phantom: true },
  { feature: "Hardened encrypted messaging apps", degoogled: false, stealth: true, phantom: true },
  { feature: "Duress PIN & panic wipe", degoogled: false, stealth: true, phantom: true },

  { feature: "Custom hardened GrapheneOS profile", degoogled: false, stealth: false, phantom: true },
  { feature: "Multi-profile compartmentalization", degoogled: false, stealth: false, phantom: true },
  { feature: "Hidden vault + decoy mode", degoogled: false, stealth: false, phantom: true },
  { feature: "Hardware sensor kill-switches", degoogled: false, stealth: false, phantom: true },
  { feature: "Self-destruct duress protocol", degoogled: false, stealth: false, phantom: true },
  { feature: "Encrypted cloud key-escrow", degoogled: false, stealth: false, phantom: true },
  { feature: "Lifetime priority support", degoogled: false, stealth: false, phantom: true },
];

const Tiers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    // Handle hash anchors
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, []);

  return (
    <main className="min-h-screen bg-black text-white pt-24">
      {/* Hero */}
      <section className="relative pb-10">
        <div
          className="absolute inset-0 opacity-40 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGES.cyber})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/80 to-black" />
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="chip">
            <Lock className="h-3 w-3" /> Compare tiers
          </div>
          <h1 className="mt-4 font-headline text-5xl md:text-7xl font-bold leading-[0.95] text-gradient-purple">
            Three tiers.<br />One uncompromising standard.
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            Every Phantom Industries starts with a hardened GrapheneOS baseline. From
            there, choose the level of invisibility that matches your threat model.
          </p>
        </div>
      </section>

      {/* Compare Models grid — stacked cards */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {TIERS.map((t, idx) => {
            const Ico = tierIcon[t.id];
            return (
              <div
                key={t.id}
                id={t.id}
                className={`relative rounded-3xl p-8 card-glass ${t.glow} flex flex-col`}
              >
                {t.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 chip bg-purple-500 text-black border-purple-400">
                    Most chosen
                  </div>
                )}

                {/* Tier wordmark logo — the visual identity of this tier */}
                <div className="flex items-center justify-center h-24">
                  <TierWordmark tierId={t.id} size="lg" />
                </div>

                <div className="mt-5 flex items-center gap-3">
                  <Ico className="h-4 w-4" style={{ color: t.color }} />
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40">
                    Tier {["I", "II", "III"][idx]} · {t.tagline.split("—")[1]?.trim()}
                  </div>
                </div>
                <p className="mt-3 text-white/60 text-sm leading-relaxed">{t.description}</p>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="text-white/50 text-sm">+</span>
                  <span className="font-display text-4xl font-bold text-white">
                    {formatAUD(t.price_add)}
                  </span>
                  <span className="text-white/50 text-sm">on device</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {t.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 text-sm text-white/75">
                      <span className="mt-0.5 h-4 w-4 rounded-full bg-purple-500/15 border border-purple-500/40 flex items-center justify-center flex-shrink-0">
                        <Check className="h-2.5 w-2.5 text-purple-300" />
                      </span>
                      {h}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/shop"
                  className={`mt-8 inline-flex items-center justify-center gap-2 py-3 rounded-full transition-all ${
                    idx === 2
                      ? "bg-purple-500 text-black hover:bg-purple-400"
                      : idx === 1
                      ? "bg-white/10 text-white hover:bg-white/20 border border-purple-500/40"
                      : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                  } text-sm font-medium`}
                >
                  Configure {t.name} device <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Feature matrix table */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="chip mx-auto">Feature matrix</div>
            <h2 className="mt-4 font-headline text-3xl md:text-5xl font-bold text-gradient-purple">
              Every feature. Every tier. Side by side.
            </h2>
          </div>

          <div className="rounded-3xl overflow-hidden card-glass">
            {/* Header row */}
            <div className="grid grid-cols-4 border-b border-white/10 bg-white/[0.02]">
              <div className="p-5 text-xs uppercase tracking-[0.2em] text-white/40">Feature</div>
              {TIERS.map((t, i) => (
                <div
                  key={t.id}
                  className={`p-4 text-center border-l border-white/5 ${
                    i === 2 ? "bg-purple-500/5" : ""
                  }`}
                >
                  <div className="flex items-center justify-center h-12">
                    <TierWordmark tierId={t.id} size="sm" />
                  </div>
                  <div className="mt-2 text-xs text-white/50">+{formatAUD(t.price_add)}</div>
                </div>
              ))}
            </div>

            {COMPARE_MATRIX.map((row, i) => (
              <div
                key={row.feature}
                className={`grid grid-cols-4 border-b border-white/5 last:border-none ${
                  i % 2 === 0 ? "bg-white/[0.01]" : ""
                }`}
              >
                <div className="p-4 md:p-5 text-sm text-white/80">{row.feature}</div>
                <Cell has={row.degoogled} />
                <Cell has={row.stealth} />
                <Cell has={row.phantom} highlight />
              </div>
            ))}
          </div>

          <div className="mt-8 text-center text-xs text-white/40">
            Every device is inspected, sealed and shipped in tamper-evident packaging.
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-headline text-4xl md:text-6xl font-bold text-gradient-phantom">
            Ready to disappear?
          </h2>
          <p className="mt-4 text-white/60">
            Pick a device. Pick a tier. We build it. You go dark.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/shop" className="btn-primary">
              Start configuring <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-ghost">
              Talk to a specialist
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

const Cell = ({ has, highlight }) => (
  <div
    className={`p-4 md:p-5 border-l border-white/5 flex items-center justify-center ${
      highlight ? "bg-purple-500/5" : ""
    }`}
  >
    {has ? (
      <span className="h-6 w-6 rounded-full bg-purple-500/15 border border-purple-500/40 flex items-center justify-center">
        <Check className="h-3.5 w-3.5 text-purple-300" />
      </span>
    ) : (
      <Minus className="h-4 w-4 text-white/20" />
    )}
  </div>
);

export default Tiers;
